/**
 * <c-header> — テーマ非依存のヘッダー
 *
 * Focal の <store-header> / <cart-count> / <popover-content> を使わずに、
 * SPドロワーの開閉・カート件数の反映・スクロール追従を自前で持つ。
 *
 * やること:
 *   1. ハンバーガー → ドロワーの開閉（フォーカストラップ・背面スクロール固定・Escで閉じる）
 *   2. カート件数の反映（初回 /cart.js、以降は cart:updated を購読）
 *   3. スクロールで隠す／出す（schema で ON のときだけ）
 *
 * ドロワーの中のメニュー開閉は <c-accordion> に任せる（snippets/accordion/）。
 */
if (!customElements.get('c-header')) {
  customElements.define(
    'c-header',
    class CHeader extends HTMLElement {
      connectedCallback() {
        this.drawer = this.querySelector('[data-header-drawer]');
        this.openBtn = this.querySelector('[data-header-open]');
        this.closeBtns = this.querySelectorAll('[data-header-close]');
        this.counts = this.querySelectorAll('[data-cart-count]');

        this.onOpen = this.onOpen.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onCartUpdated = this.onCartUpdated.bind(this);

        this.openBtn?.addEventListener('click', this.onOpen);
        this.closeBtns.forEach((b) => b.addEventListener('click', this.onClose));
        document.addEventListener('keydown', this.onKeydown);

        // Focal など多くのテーマが cart:updated を投げる。載っていないテーマでも初回取得は効く
        document.documentElement.addEventListener('cart:updated', this.onCartUpdated);
        // Dawn は cart:updated ではなく pubsub（PUB_SUB_EVENTS.cartUpdate）で知らせる。
        // カートに追加（product-form.js）・ドロワーでの数量変更（cart.js）のあとに件数を取り直す
        // ⚠️ constants.js / pubsub.js のトップレベル const・function は window に載らないので typeof で見る。
        //    読み込み順で未定義のときは DOMContentLoaded（defer のスクリプトが全部読み終わった後）で取り直す
        const subscribeCartUpdate = () => {
          if (this.unsubscribeCartUpdate) return;
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCartCount());
          }
        };
        subscribeCartUpdate();
        if (!this.unsubscribeCartUpdate) document.addEventListener('DOMContentLoaded', subscribeCartUpdate, { once: true });
        this.refreshCartCount();

        if (this.hasAttribute('sticky')) this.initSticky();

        // お気に入りアプリはハートを後から差し込み、件数も書き換える。そのたびにドロワーの分を写し直す
        const icons = this.querySelector('.header__icons');
        if (icons && this.drawer?.querySelector('[data-drawer-wishlist]')) {
          this.wishlistObserver = new MutationObserver(() => this.syncDrawerWishlist());
          this.wishlistObserver.observe(icons, { childList: true, subtree: true, characterData: true });
          this.syncDrawerWishlist();
        }
      }

      disconnectedCallback() {
        this.wishlistObserver?.disconnect();
        document.removeEventListener('keydown', this.onKeydown);
        document.documentElement.removeEventListener('cart:updated', this.onCartUpdated);
        this.unsubscribeCartUpdate?.();
        this.releaseScroll();
      }

      /* --- ドロワー --- */

      // ドロワーのお気に入り（ハート）：ヘッダーにアプリ（Wishlist by Square）のハートがあるときだけ出す。
      // リンク先と件数は、アプリが差し込んだものから写す（差し込み・件数の変化を見て写し直す。開くときにも念のため見る）
      syncDrawerWishlist() {
        const link = this.drawer?.querySelector('[data-drawer-wishlist]');
        if (!link) return;
        const source = this.querySelector('.header__icons .Vtl-WishlistHeader');
        link.hidden = !source;
        if (!source) return;
        const href = source.getAttribute('href');
        if (href) link.setAttribute('href', href);
        const count = link.querySelector('[data-drawer-wishlist-count]');
        const number = parseInt(source.querySelector('.Vtl-WishlistHeader__HeartCounter')?.textContent || '0', 10) || 0;
        if (count) {
          count.textContent = number;
          count.hidden = number < 1;
        }
      }

      onOpen() {
        this.syncDrawerWishlist();
        this.drawer?.setAttribute('open', '');
        this.openBtn?.setAttribute('aria-expanded', 'true');
        this.lockScroll();
        // 開いたら中の最初の操作要素へ移す（キーボード操作の起点）
        // ⚠️ preventScroll が無いと、まだ画面の外にあるパネルの中へフォーカスした瞬間に、ブラウザがドロワーの枠
        //    （overflow: hidden）を横へスクロールして見せようとする。パネルが滑らずにいきなり出て、暗幕のほうが横へ動く
        this.drawer?.querySelector('a, button')?.focus({ preventScroll: true });
        if (this.drawer) this.drawer.scrollLeft = 0;
      }

      onClose() {
        this.drawer?.removeAttribute('open');
        this.openBtn?.setAttribute('aria-expanded', 'false');
        this.releaseScroll();
        this.openBtn?.focus();
      }

      onKeydown(event) {
        if (!this.drawer?.hasAttribute('open')) return;

        if (event.key === 'Escape') {
          this.onClose();
          return;
        }

        // フォーカストラップ：ドロワーの外へ出さない
        if (event.key !== 'Tab') return;
        const focusables = this.drawer.querySelectorAll(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      lockScroll() {
        // 横のがたつきは html の scrollbar-gutter: stable（c-variable.css）で防いでいる。
        // ここで body に padding-right を足すと、閉じる瞬間にページがスクロールバーの幅ぶん横へずれる
        document.body.style.overflow = 'hidden';
      }

      releaseScroll() {
        document.body.style.overflow = '';
      }

      /* --- カート件数 --- */

      async refreshCartCount() {
        if (!this.counts.length) return;
        const root = window.Shopify?.routes?.root || '/';
        try {
          const cart = await fetch(`${root}cart.js`).then((r) => r.json());
          this.setCartCount(cart.item_count);
        } catch (e) {
          /* 取得できなくても表示は壊さない */
        }
      }

      onCartUpdated(event) {
        const count = event.detail?.cart?.item_count;
        if (typeof count === 'number') this.setCartCount(count);
        else this.refreshCartCount();
      }

      setCartCount(count) {
        this.counts.forEach((el) => {
          el.textContent = count;
          el.hidden = count === 0;
        });
      }

      /* --- スクロール追従 --- */

      initSticky() {
        let lastY = window.scrollY;
        let ticking = false;

        const update = () => {
          const y = window.scrollY;
          // 下へスクロール中は隠す。ただしドロワーが開いているときは動かさない
          // hide-on-scroll 属性があるときだけ（Mt.SUMI は常に固定＝付けない。2026-09-24）
          if (this.hasAttribute('hide-on-scroll') && !this.drawer?.hasAttribute('open')) {
            this.toggleAttribute('hidden-by-scroll', y > lastY && y > this.offsetHeight);
          }
          this.toggleAttribute('scrolled', y > 0);
          lastY = y;
          ticking = false;
        };

        window.addEventListener(
          'scroll',
          () => {
            if (ticking) return;
            ticking = true;
            // scroll は毎フレーム飛ぶので間引く
            requestAnimationFrame(update);
          },
          { passive: true }
        );
      }
    }
  );
}
